import type {ReactElement} from 'react';
import {useCallback, useMemo} from 'react';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import type {SelectedContextualMenuItems} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {flattenContextualMenuOptions} from '@Components/ai-generation-box/ai-generation-box.library';
import {Text, TextSize} from '@Components/text';
import type {AiGeneratedContent, SelectedValueType} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import type {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import {getPromptBoxOptions} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {getRecentlyGeneratedTranscripts, saveSelectedWriting, showTextToSpeechAiWriterUpsellDialog, writeTranscript} from '@Libraries/text-to-speech.library';
import {isUserPremium} from '@Libraries/user.library';
import {useAiWriterPersistOnSelect} from '@Components/ai-writer/hooks/use-ai-writer-persist-on-select';
import {getLengthAndToneContextualMenuGroups} from '@Components/ai-writer/ai-writer.library';
import type {AiWriterSource} from '@Components/ai-writer/ai-writer.types';
import {
  AI_WRITER_CONTEXTUAL_MENU_WIDTH,
  AI_WRITER_PROMPT_BOX_ID,
  AI_WRITER_SESSION_STORAGE_KEY,
  RECENT_TRANSCRIPTS_PAGE_SIZE,
} from '@Components/panels/ai-voice-panel/components/ai-writer-panel-view/ai-writer-panel-view.types';

interface UseAiVoiceWriterParams {
  onContentSelected: (text: string) => void;
}

export function useAiVoiceWriter({onContentSelected}: UseAiVoiceWriterParams): AiWriterSource<AiGeneratedContent> {
  const initialConfig = useMemo(() => {
    return {
      ...getPromptBoxOptions(),
      contextualMenu: getLengthAndToneContextualMenuGroups(),
      promptBoxState: PromptBoxState.ACTIVE,
    };
  }, []);

  const fetchRecents = useCallback((): Promise<AiGeneratedContent[]> => {
    return getRecentlyGeneratedTranscripts(RECENT_TRANSCRIPTS_PAGE_SIZE, 0);
  }, []);

  const persist = useCallback(async (item: AiGeneratedContent): Promise<void> => {
    if (item.idPrompt) {
      await saveSelectedWriting(item.idPrompt, item.text);
    }
  }, []);

  const onSelected = useCallback(
    (item: AiGeneratedContent): void => {
      onContentSelected(item.text);
    },
    [onContentSelected]
  );

  const onItemSelect = useAiWriterPersistOnSelect<AiGeneratedContent>({persist, onSelected});

  const onGenerate = useCallback(async (prompt: string, options: SelectedContextualMenuItems, _originator: GenerateIdeasOriginator): Promise<AiGeneratedContent[]> => {
    return writeTranscript(prompt, flattenContextualMenuOptions(options) as SelectedValueType);
  }, []);

  const canGenerate = useCallback((): boolean => {
    if (!isUserPremium()) {
      void showTextToSpeechAiWriterUpsellDialog();
      return false;
    }
    return true;
  }, []);

  const renderItem = useCallback((item: AiGeneratedContent): ReactElement => {
    return <Text val={item.text} size={TextSize.MD} className="content-body" />;
  }, []);

  const getContextualMenu: FooterLeftAction = useCallback((props): ReactElement => {
    return <AiPromptBoxContextualMenu {...props} maxContextualMenuWidth={AI_WRITER_CONTEXTUAL_MENU_WIDTH} />;
  }, []);

  return useMemo(
    () => ({
      id: AI_WRITER_PROMPT_BOX_ID,
      sessionStorageKey: AI_WRITER_SESSION_STORAGE_KEY,
      initialConfig,
      options: [getContextualMenu],
      fetchRecents,
      onGenerate,
      canGenerate,
      onItemSelect,
      getItemKey: (item: AiGeneratedContent) => item.text,
      renderItem,
      aiPromptBoxPreviewText: window.i18next.t('pmwjs_text_to_speech_ai_writer_placeholder'),
      title: window.i18next.t('pmwjs_write_with_ai'),
      showRetryOnError: true,
    }),
    [initialConfig, getContextualMenu, fetchRecents, onGenerate, canGenerate, onItemSelect, renderItem]
  );
}
